import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

export type InflowCategory = 'capital_injection' | 'loan_proceeds' | 'loan_repayment' | 'asset_sale' | 'grant' | 'refund' | 'cost_reimbursement' | 'other';

export const INFLOW_CATEGORY_LABELS: Record<InflowCategory, string> = {
  capital_injection: 'Capital Injection',
  loan_proceeds: 'Loan Proceeds',
  loan_repayment: 'Loan Repayment (Received)',
  asset_sale: 'Asset Sale',
  grant: 'Grant',
  refund: 'Refund',
  cost_reimbursement: 'Cost Reimbursement',
  other: 'Other',
};

export interface CashInflow {
  id: string;
  inflow_number: string;
  inflow_date: string;
  category: InflowCategory;
  amount: number;
  currency: string;
  bank_account_id: string | null;
  description: string | null;
  reference_number: string | null;
  source_name: string | null;
  cash_flow_type: string;
  business_unit: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  bank_account?: { id: string; name: string; bank_name: string | null } | null;
}

export interface CreateCashInflowInput {
  inflow_date: string;
  category: InflowCategory;
  amount: number;
  currency?: string;
  bank_account_id?: string | null;
  description?: string;
  reference_number?: string;
  source_name?: string;
  cash_flow_type?: string;
  business_unit?: string;
}

export const useCashInflows = (filters?: {
  date_from?: string;
  date_to?: string;
  category?: string;
  bank_account_id?: string;
}) => {
  return useQuery({
    queryKey: ['cash-inflows', filters],
    queryFn: async () => {
      let query = supabase
        .from('cash_inflows' as any)
        .select('*, bank_account:bank_accounts(id, name, bank_name)')
        .order('inflow_date', { ascending: false });

      if (filters?.date_from) {
        query = query.gte('inflow_date', filters.date_from);
      }
      if (filters?.date_to) {
        query = query.lte('inflow_date', filters.date_to);
      }
      if (filters?.category) {
        query = query.eq('category', filters.category);
      }
      if (filters?.bank_account_id) {
        query = query.eq('bank_account_id', filters.bank_account_id);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as unknown as CashInflow[];
    },
  });
};

export const useCashInflow = (id: string) => {
  return useQuery({
    queryKey: ['cash-inflow', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cash_inflows' as any)
        .select('*, bank_account:bank_accounts(id, name, bank_name)')
        .eq('id', id)
        .single();
      if (error) throw error;
      return data as unknown as CashInflow;
    },
    enabled: !!id,
  });
};

export const useCreateCashInflow = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: CreateCashInflowInput) => {
      const { data: numberData, error: numberError } = await supabase
        .rpc('generate_inflow_number');
      if (numberError) throw numberError;

      const { data, error } = await supabase
        .from('cash_inflows' as any)
        .insert({
          ...input,
          inflow_number: numberData,
          created_by: user?.id,
        })
        .select()
        .single();
      if (error) throw error;
      return data as unknown as CashInflow;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash-inflows'] });
      toast.success('Cash inflow recorded successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to record cash inflow: ${error.message}`);
    },
  });
};

export const useUpdateCashInflow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...input }: Partial<CashInflow> & { id: string }) => {
      const { data, error } = await supabase
        .from('cash_inflows' as any)
        .update(input)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as unknown as CashInflow;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash-inflows'] });
      queryClient.invalidateQueries({ queryKey: ['cash-inflow'] });
      toast.success('Cash inflow updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update cash inflow: ${error.message}`);
    },
  });
};

export const useDeleteCashInflow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('cash_inflows' as any)
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash-inflows'] });
      toast.success('Cash inflow deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete cash inflow: ${error.message}`);
    },
  });
};
